import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Mail, Phone, MapPin, Linkedin, Instagram } from 'lucide-react';
import { companyApi, brandsApi } from '../../services/api';

const Footer = () => {
  const [company, setCompany] = useState(null);
  const [brands, setBrands] = useState([]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [companyRes, brandsRes] = await Promise.all([
          companyApi.get(),
          brandsApi.getAll(),
        ]);
        setCompany(companyRes.data);
        setBrands(brandsRes.data);
      } catch (error) {
        console.error('Error fetching footer data:', error);
      }
    };
    fetchData();
  }, []);

  return (
    <footer className="bg-gray-900 text-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Company Info */}
          <div>
            {company?.logo && (
              <img
                src={company.logo}
                alt={company.name}
                className="h-10 w-auto mb-6 brightness-0 invert"
              />
            )}
            <p className="text-gray-400 text-sm leading-relaxed mb-6">
              {company?.tagline}
            </p>
            <div className="flex space-x-4">
              {company?.social?.linkedin && (
                <a
                  href={company.social.linkedin}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="p-2 bg-gray-800 rounded-lg hover:bg-gray-700 transition-colors"
                >
                  <Linkedin className="h-5 w-5" />
                </a>
              )}
              {company?.social?.instagram && (
                <a
                  href={company.social.instagram}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="p-2 bg-gray-800 rounded-lg hover:bg-gray-700 transition-colors"
                >
                  <Instagram className="h-5 w-5" />
                </a>
              )}
            </div>
          </div>

          {/* Brands */}
          <div>
            <h3 className="text-sm font-semibold uppercase tracking-wider mb-6">Our Brands</h3>
            <ul className="space-y-3">
              {brands.map((brand) => (
                <li key={brand.id}>
                  <Link
                    to={`/brand/${brand.id}`}
                    className="text-gray-400 hover:text-white transition-colors text-sm"
                  >
                    {brand.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="text-sm font-semibold uppercase tracking-wider mb-6">Quick Links</h3>
            <ul className="space-y-3">
              <li>
                <Link to="/" className="text-gray-400 hover:text-white transition-colors text-sm">
                  Home
                </Link>
              </li>
              <li>
                <Link to="/news" className="text-gray-400 hover:text-white transition-colors text-sm">
                  News
                </Link>
              </li>
              <li>
                <Link to="/about" className="text-gray-400 hover:text-white transition-colors text-sm">
                  About Us
                </Link>
              </li>
              <li>
                <Link to="/contact" className="text-gray-400 hover:text-white transition-colors text-sm">
                  Contact
                </Link>
              </li>
              <li>
                <Link to="/admin" className="text-gray-400 hover:text-white transition-colors text-sm">
                  Admin Panel
                </Link>
              </li>
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h3 className="text-sm font-semibold uppercase tracking-wider mb-6">Contact Us</h3>
            <ul className="space-y-4">
              {company?.contact?.email && (
                <li>
                  <a
                    href={`mailto:${company.contact.email}`}
                    className="flex items-center text-gray-400 hover:text-white transition-colors text-sm"
                  >
                    <Mail className="h-4 w-4 mr-3 flex-shrink-0" />
                    {company.contact.email}
                  </a>
                </li>
              )}
              {company?.contact?.phone && (
                <li>
                  <a
                    href={`tel:${company.contact.phone}`}
                    className="flex items-center text-gray-400 hover:text-white transition-colors text-sm"
                  >
                    <Phone className="h-4 w-4 mr-3 flex-shrink-0" />
                    {company.contact.phone}
                  </a>
                </li>
              )}
              {company?.contact?.address && (
                <li>
                  <span className="flex items-start text-gray-400 text-sm">
                    <MapPin className="h-4 w-4 mr-3 flex-shrink-0 mt-0.5" />
                    {company.contact.address}
                  </span>
                </li>
              )}
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="mt-16 pt-8 border-t border-gray-800">
          <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
            <p className="text-gray-500 text-sm">
              © {new Date().getFullYear()} {company?.name || 'Mesh Lighting Ltd'}. All rights reserved.
            </p>
            <div className="flex space-x-6">
              <a href="#" className="text-gray-500 hover:text-gray-400 text-sm transition-colors">
                Privacy Policy
              </a>
              <a href="#" className="text-gray-500 hover:text-gray-400 text-sm transition-colors">
                Terms of Service
              </a>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
